type TileDefinition = {
  sprite: string
  id: number
  show: boolean
  spawn?: boolean
}

export type CampaignDictionary = {
  tiles: Record<string, TileDefinition>
}

export type CampaignEventTrigger = {
  type: string
  id?: string
}

export type CampaignEventAction = {
  type: string
  [key: string]: unknown
}

export type CampaignEventCondition = {
  type: string
  [key: string]: unknown
}

export type CampaignTranslations = Record<string, string>

export type CampaignEventDefinition = {
  id: string
  token: string
  position?: string
  trigger?: CampaignEventTrigger
  actions?: CampaignEventAction[]
  conditions?: CampaignEventCondition[]
}

export type CampaignEvents = Record<string, CampaignEventDefinition[]>

const CAMPAIGN_THEME_GLOB = import.meta.glob('../../game/content/*/campaign-theme.wav', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

const CAMPAIGN_MAP_GLOB = import.meta.glob('../../game/content/*/map.csv', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>

const CAMPAIGN_DICTIONARY_GLOB = import.meta.glob('../../game/content/*/dictionary.json', {
  eager: true,
  import: 'default',
}) as Record<string, CampaignDictionary>

const CAMPAIGN_EVENTS_GLOB = import.meta.glob('../../game/content/*/events.json', {
  eager: true,
  import: 'default',
}) as Record<string, CampaignEvents>

const CAMPAIGN_EVENT_TOKENS_GLOB = import.meta.glob('../../game/content/*/tokens/*.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

const CAMPAIGN_I18N_GLOB = import.meta.glob('../../game/content/*/i18n.*.json', {
  eager: true,
  import: 'default',
}) as Record<string, CampaignTranslations>

const CAMPAIGN_VOICE_GLOB = import.meta.glob('../../game/content/*/vo/*', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

const CAMPAIGN_SFX_GLOB = import.meta.glob('../../game/content/*/sfx/*', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

const CAMPAIGN_TILES_GLOB = import.meta.glob('../../game/content/*/tiles/*.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

const extractCampaignId = (path: string): string | undefined => {
  const segments = path.split('/')
  const contentIndex = segments.findIndex((segment) => segment === 'content')

  if (contentIndex >= 0 && segments.length > contentIndex + 1) {
    return segments[contentIndex + 1]
  }

  return undefined
}

const CAMPAIGN_THEME_BY_ID = Object.entries(CAMPAIGN_THEME_GLOB).reduce<Record<string, string>>((acc, [path, url]) => {
  const campaignId = extractCampaignId(path)

  if (campaignId) {
    acc[campaignId] = url
  }

  return acc
}, {})

const CAMPAIGN_MAP_BY_ID = Object.entries(CAMPAIGN_MAP_GLOB).reduce<Record<string, string>>((acc, [path, data]) => {
  const campaignId = extractCampaignId(path)

  if (campaignId) {
    acc[campaignId] = data
  }

  return acc
}, {})

const CAMPAIGN_DICTIONARY_BY_ID = Object.entries(CAMPAIGN_DICTIONARY_GLOB).reduce<Record<string, CampaignDictionary>>(
  (acc, [path, dictionary]) => {
    const campaignId = extractCampaignId(path)

    if (campaignId) {
      acc[campaignId] = dictionary
    }

    return acc
  },
  {}
)

const CAMPAIGN_EVENTS_BY_ID = Object.entries(CAMPAIGN_EVENTS_GLOB).reduce<Record<string, CampaignEvents>>(
  (acc, [path, events]) => {
    const campaignId = extractCampaignId(path)

    if (campaignId) {
      acc[campaignId] = events
    }

    return acc
  },
  {}
)

const CAMPAIGN_EVENT_TOKENS_BY_ID = Object.entries(CAMPAIGN_EVENT_TOKENS_GLOB).reduce<
  Record<string, Record<string, string>>
>((acc, [path, url]) => {
  const campaignId = extractCampaignId(path)

  if (!campaignId) {
    return acc
  }

  const segments = path.split('/')
  const fileName = segments[segments.length - 1]
  const tokenKey = fileName.replace(/\.[^/.]+$/, '')

  if (!acc[campaignId]) {
    acc[campaignId] = {}
  }

  acc[campaignId][tokenKey] = url

  return acc
}, {})

const CAMPAIGN_TRANSLATIONS_BY_ID = Object.entries(CAMPAIGN_I18N_GLOB).reduce<
  Record<string, Record<string, CampaignTranslations>>
>((acc, [path, translations]) => {
  const campaignId = extractCampaignId(path)

  if (!campaignId) {
    return acc
  }

  const localeMatch = path.match(/i18n\.([^.]+)\.json$/)

  if (!localeMatch) {
    return acc
  }

  const locale = localeMatch[1]

  if (!acc[campaignId]) {
    acc[campaignId] = {}
  }

  acc[campaignId][locale] = translations

  return acc
}, {})

const reduceAudioGlob = (glob: Record<string, string>) =>
  Object.entries(glob).reduce<Record<string, Record<string, string>>>((acc, [path, url]) => {
    const campaignId = extractCampaignId(path)

    if (!campaignId) {
      return acc
    }

    const segments = path.split('/')
    const fileName = segments[segments.length - 1]
    const key = fileName.replace(/\.[^/.]+$/, '')

    if (!acc[campaignId]) {
      acc[campaignId] = {}
    }

    acc[campaignId][key] = url

    return acc
  }, {})

const CAMPAIGN_VOICE_BY_ID = reduceAudioGlob(CAMPAIGN_VOICE_GLOB)
const CAMPAIGN_SFX_BY_ID = reduceAudioGlob(CAMPAIGN_SFX_GLOB)

const CAMPAIGN_TILES_BY_ID = Object.entries(CAMPAIGN_TILES_GLOB).reduce<Record<string, Record<string, string>>>(
  (acc, [path, url]) => {
    const campaignId = extractCampaignId(path)

    if (!campaignId) {
      return acc
    }

    const segments = path.split('/')
    const fileName = segments[segments.length - 1]
    const tileKey = fileName.replace(/\.[^/.]+$/, '')

    if (!acc[campaignId]) {
      acc[campaignId] = {}
    }

    acc[campaignId][tileKey] = url

    return acc
  },
  {}
)

export const getCampaignThemeUrl = (campaignId: string): string | undefined => {
  return CAMPAIGN_THEME_BY_ID[campaignId]
}

export const getCampaignMapData = (campaignId: string): string | undefined => {
  return CAMPAIGN_MAP_BY_ID[campaignId]
}

export const getCampaignDictionary = (campaignId: string): CampaignDictionary | undefined => {
  return CAMPAIGN_DICTIONARY_BY_ID[campaignId]
}

export const getCampaignTileUrls = (campaignId: string): Record<string, string> => {
  return CAMPAIGN_TILES_BY_ID[campaignId] ?? {}
}

export const getCampaignTileTextureKey = (campaignId: string, tileSprite: string): string => {
  return `campaign:${campaignId}:tile:${tileSprite}`
}

export const getCampaignTilesDictionary = (campaignId: string): CampaignDictionary | undefined => {
  return CAMPAIGN_DICTIONARY_BY_ID[campaignId]
}

export const getCampaignEvents = (campaignId: string): CampaignEvents | undefined => {
  return CAMPAIGN_EVENTS_BY_ID[campaignId]
}

export const getCampaignEventTokenUrls = (campaignId: string): Record<string, string> => {
  return CAMPAIGN_EVENT_TOKENS_BY_ID[campaignId] ?? {}
}

export const getCampaignEventTokenTextureKey = (campaignId: string, tokenKey: string): string => {
  return `campaign:${campaignId}:event-token:${tokenKey}`
}

export const getCampaignTranslations = (campaignId: string, locale: string): CampaignTranslations | undefined => {
  return CAMPAIGN_TRANSLATIONS_BY_ID[campaignId]?.[locale]
}

export const getCampaignTranslation = (campaignId: string, locale: string, key: string): string | undefined => {
  return getCampaignTranslations(campaignId, locale)?.[key]
}

export const getCampaignVoiceUrls = (campaignId: string): Record<string, string> => {
  return CAMPAIGN_VOICE_BY_ID[campaignId] ?? {}
}

export const getCampaignSfxUrls = (campaignId: string): Record<string, string> => {
  return CAMPAIGN_SFX_BY_ID[campaignId] ?? {}
}

export const getCampaignVoiceAudioKey = (campaignId: string, voiceKey: string): string => {
  return `campaign:${campaignId}:voice:${voiceKey}`
}

export const getCampaignSfxAudioKey = (campaignId: string, sfxKey: string): string => {
  return `campaign:${campaignId}:sfx:${sfxKey}`
}
